import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Calendar, Clock, ArrowRight, Search, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { highlightTextWithCount, fuzzyMatch } from "@/utils/highlightText";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  category: string;
  author_name: string;
  author_image_url: string | null;
  featured_image_url: string | null;
  published_at: string;
  reading_time: number;
}

const categoryNames: Record<string, string> = {
  "cybersecurity-basics": "Cyber Security Basics",
  "threat-intelligence": "Threat Intelligence",
  "compliance-regulations": "Compliance & Regulations",
  "risk-management": "Risk Management",
  "security-strategy": "Security Strategy",
  "small-business-security": "Small Business Security",
};

const categoryColors: Record<string, string> = {
  "cybersecurity-basics": "bg-blue-500/10 text-blue-600 hover:bg-blue-500/20",
  "threat-intelligence": "bg-red-500/10 text-red-600 hover:bg-red-500/20",
  "compliance-regulations": "bg-green-500/10 text-green-600 hover:bg-green-500/20",
  "risk-management": "bg-orange-500/10 text-orange-600 hover:bg-orange-500/20",
  "security-strategy": "bg-purple-500/10 text-purple-600 hover:bg-purple-500/20",
  "small-business-security": "bg-cyan-500/10 text-cyan-600 hover:bg-cyan-500/20",
};

const Insights = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    let filtered = posts;
    
    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }
    
    // Filter by search query (with fuzzy matching)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.author_name.toLowerCase().includes(query) ||
        (post.content && post.content.toLowerCase().includes(query)) ||
        fuzzyMatch(post.title, query) ||
        fuzzyMatch(post.excerpt, query) ||
        (post.content && fuzzyMatch(post.content, query))
      );
    }
    
    setFilteredPosts(filtered);
  }, [selectedCategory, posts, searchQuery]);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("status", "published")
        .not("published_at", "is", null)
        .order("published_at", { ascending: false });

      if (error) throw error;
      setPosts(data || []);
      setFilteredPosts(data || []);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      toast({
        title: "Error",
        description: "Failed to load blog posts. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "4FOX Security Insights",
    "description": "Expert insights on cyber security, compliance, and risk management for UK businesses",
    "url": "https://4fox.uk/insights",
    "publisher": {
      "@type": "Organization",
      "name": "4FOX Security",
      "url": "https://4fox.uk"
    }
  };

  return (
    <>
      <SEO
        title="Security Insights & Expert Articles | 4FOX Cyber Security Blog"
        description="Expert insights on cyber security, threat intelligence, compliance, and risk management. Practical security guidance for UK businesses of all sizes."
        canonical="https://4fox.uk/insights"
        schema={schema}
      />
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        
        <main className="pt-24 pb-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                Security Insights
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Expert guidance on cyber security, compliance, and risk management for UK businesses
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search articles by title, content, author..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10 h-12 text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  Found {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'} matching "{searchQuery}"
                </p>
              )}
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 justify-center mb-12">
              <Badge
                variant={selectedCategory === null ? "default" : "outline"}
                className="cursor-pointer px-4 py-2 text-sm"
                onClick={() => setSelectedCategory(null)}
              >
                All Articles
              </Badge>
              {Object.entries(categoryNames).map(([key, name]) => (
                <Badge
                  key={key}
                  variant={selectedCategory === key ? "default" : "outline"}
                  className="cursor-pointer px-4 py-2 text-sm"
                  onClick={() => setSelectedCategory(key)}
                >
                  {name}
                </Badge>
              ))}
            </div>

            {/* Blog Posts Grid */}
            {isLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48 w-full" />
                    <CardHeader>
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-full" />
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">
                  {searchQuery 
                    ? `No articles found matching "${searchQuery}"${selectedCategory ? ` in ${categoryNames[selectedCategory]}` : ''}. Try different keywords or clear the search.`
                    : `No articles found in this category yet. Check back soon!`
                  }
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => (
                  <Link key={post.id} to={`/insights/${post.slug}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden group">
                      {post.featured_image_url ? (
                        <div className="h-48 overflow-hidden bg-muted flex items-center justify-center">
                          <img
                            src={post.featured_image_url}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                  <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
                                    <div class="text-center p-6">
                                      <svg class="w-16 h-16 mx-auto text-muted-foreground/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <p class="text-xs text-muted-foreground mt-2">${categoryNames[post.category]}</p>
                                    </div>
                                  </div>
                                `;
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div className="h-48 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                          <div className="text-center p-6">
                            <svg className="w-16 h-16 mx-auto text-muted-foreground/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-xs text-muted-foreground mt-2">{categoryNames[post.category]}</p>
                          </div>
                        </div>
                      )}
                      <CardHeader>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className={categoryColors[post.category]}>
                            {categoryNames[post.category]}
                          </Badge>
                        </div>
                        <CardTitle className="group-hover:text-primary transition-colors line-clamp-2 leading-snug min-h-[3.5rem] py-1">
                          {searchQuery ? highlightTextWithCount(post.title, searchQuery, true).jsx : post.title}
                        </CardTitle>
                        <CardDescription className="line-clamp-3">
                          {searchQuery ? highlightTextWithCount(post.excerpt, searchQuery, true).jsx : post.excerpt}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(post.published_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {post.reading_time} min
                            </span>
                          </div>
                          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Insights;
